/**
 * Pure, deterministic helpers for computing the radar frame list and
 * classifying data freshness. No network access here — see radarApi.ts
 * for I/O. Kept pure so it can be unit tested without a DOM or fetch.
 */

/** Number of historical frames requested from the source (per DECISIONS D-002 / DESIGN §7). */
export const FRAME_COUNT = 12;

/** Step between frames in milliseconds — source advertises a ~5 min cadence (RESEARCH.md §1, opcja B). */
export const FRAME_STEP_MS = 5 * 60 * 1000;

/**
 * Freshness thresholds in milliseconds (DESIGN.md §9).
 * "Fresh" is 15 min (not the original 10): the service renders the newest
 * mosaic ~8–12 min after the timestamp it advertises in timeInfo (measured
 * 2026-07-19), so the newest *renderable* frame is typically 10–15 min old.
 */
export const FRESHNESS_FRESH_MAX_MS = 15 * 60 * 1000;
export const FRESHNESS_AGING_MAX_MS = 30 * 60 * 1000;

/** Minimum number of successfully loaded frames before treating the app as a hard error (DESIGN.md §9). */
export const MIN_OK_FRAMES = 6;

/**
 * Extra older candidate frames requested beyond FRAME_COUNT. The service can
 * advertise timestamps whose mosaic is not rendered yet (comes back as a fully
 * transparent PNG), so we fetch a few more and drop trailing blanks.
 */
export const BLANK_FRAME_BUFFER = 3;

export interface FrameCandidate {
  timeMs: number;
  hasEchoes: boolean;
}

/**
 * ---- Candidate validation (hotfix v0.1.1) ----
 * The service advertises timestamps in timeInfo before the mosaic exists;
 * exportImage then returns an empty PNG, a partially-ingested mosaic, or a
 * byte-identical copy of the PREVIOUS product (time snapping). A frame this
 * young is therefore treated as provisional and must prove it has plausible
 * content; once it is older than RENDER_MATURITY_MS the server has had ample
 * time to render, so whatever it serves is the real product — including a
 * genuinely echo-free image. That age cutoff is what distinguishes
 * "not rendered yet" from "no rain detected", and it is also the method's
 * limit: a real nationwide echo drop within the maturity window can be
 * deferred by a few polls, and a partial mosaic that already exceeds the
 * relative threshold below can be accepted early.
 */

/** Age at which a frame is considered fully rendered server-side (measured lag 8–12 min + margin). */
export const RENDER_MATURITY_MS = 15 * 60 * 1000;

/** Absolute floor of visible sample pixels for an immature frame to be plausible. */
export const MIN_ABS_PIXELS = 8;

/** An immature frame with fewer pixels than this fraction of the window median is a partial mosaic. */
export const PARTIAL_MEDIAN_RATIO = 0.25;

/**
 * Physical-continuity guard: nationwide echo coverage cannot drop from a
 * window median above this to literal zero within one 5-min step. An EMPTY
 * frame is therefore only ever accepted when the recent window is itself
 * near-empty; otherwise it is treated as server render lag / pipeline outage
 * and deferred — the chip then honestly ages toward the stale state.
 */
export const EMPTY_PLAUSIBLE_MEDIAN_MAX = 32;

/** Maximum missing 5-min slots fetched per poll cycle (transfer safety). */
export const MAX_BACKFILL_PER_POLL = 3;

/**
 * ---- M002 phase 1: WMS source A (D-005) ----
 * Source A advertises real, irregular timestamps (~2 min cadence) instead of
 * a synthetic 5-min grid, so the window is built from the advertised list
 * directly rather than from `computeFrameTimes`/`pollCandidateTimes`.
 */

/** Target size of the rolling timeline window for source A (DECISIONS D-005: ~48 latest advertised times, ~96 min). */
export const TARGET_FRAME_COUNT = 48;

/** Frames that must be verified before the timeline is revealed (DESIGN.md Aneks M002 §A7). */
export const INITIAL_PLAYABLE_COUNT = 6;

/** Max parallel candidate requests per progressive-loading batch (politeness to a no-SLA service). */
export const LOAD_BATCH_SIZE = 6;

/** Max new advertised slots fetched per poll cycle for source A (reuses the D-004 backfill cap). */
export const POLL_MAX_NEW = MAX_BACKFILL_PER_POLL;

/**
 * Newest `target` advertised times, ascending. `advertisedMs` need not be
 * pre-sorted. Returns fewer than `target` when the source has not advertised
 * that many yet.
 */
export function selectFromAdvertised(
  advertisedMs: number[],
  target: number = TARGET_FRAME_COUNT
): number[] {
  const sorted = [...advertisedMs].sort((a, b) => a - b);
  if (sorted.length <= target) return sorted;
  return sorted.slice(sorted.length - target);
}

/**
 * Advertised times strictly newer than `latestAcceptedMs`, oldest first,
 * capped at `cap` per call. Used by the source-A poll to find newly
 * published slots (and to keep re-offering a slot that was previously
 * deferred/invalid, since it stays "missing" until it is finally accepted).
 */
export function missingAdvertised(
  advertisedMs: number[],
  latestAcceptedMs: number,
  cap: number = 3
): number[] {
  const sorted = [...advertisedMs].sort((a, b) => a - b);
  return sorted.filter((t) => t > latestAcceptedMs).slice(0, cap);
}

/**
 * True when a candidate's decoded content is byte-and-pixel identical to a
 * neighboring accepted frame — a server-side time-snapping duplicate that
 * must never be presented as its own observation (evaluateCandidate's own
 * duplicate check only fires for immature frames; this catches mature ones
 * too, independent of age).
 */
export function isDuplicateOfNeighbor(
  frame: { px: number; bytes: number },
  neighbor?: { px: number; bytes: number }
): boolean {
  if (!neighbor) return false;
  return frame.px === neighbor.px && frame.bytes === neighbor.bytes;
}

export type CandidateVerdict = "accept" | "defer" | "invalid";

export interface CandidateEvaluation {
  /** Visible pixels in the downsampled decode; -1 when the payload was not a decodable image. */
  pixelCount: number;
  byteSize: number;
  /** now − frame observation time. */
  ageMs: number;
  /** Median pixel count of currently accepted frames; 0 when unknown. */
  windowMedianPixels: number;
  /** Pixel/byte stats of the newest accepted frame, for duplicate (snapping) detection. */
  prevPixelCount?: number;
  prevByteSize?: number;
}

/**
 * Decide whether a fetched candidate frame may enter the timeline.
 * "defer" = plausibly not rendered yet — keep the last good frame and retry
 * on a later poll. "invalid" = response was not a decodable image — retry
 * with a cache-buster. Only "accept" may ever reach the timeline.
 */
export function evaluateCandidate(e: CandidateEvaluation): CandidateVerdict {
  if (e.pixelCount < 0) return "invalid";
  if (e.ageMs >= RENDER_MATURITY_MS) {
    if (e.pixelCount === 0 && e.windowMedianPixels >= EMPTY_PLAUSIBLE_MEDIAN_MAX) {
      return "defer"; // contradicts continuity: lag/outage, not a real all-clear
    }
    return "accept";
  }
  if (e.pixelCount === 0) return "defer";
  const floor = Math.max(
    MIN_ABS_PIXELS,
    Math.floor(e.windowMedianPixels * PARTIAL_MEDIAN_RATIO)
  );
  if (e.windowMedianPixels > 0 && e.pixelCount < floor) return "defer";
  if (
    e.prevByteSize !== undefined &&
    e.byteSize === e.prevByteSize &&
    e.pixelCount === e.prevPixelCount
  ) {
    return "defer"; // exportImage snapped to the previous product for this too-new time
  }
  return "accept";
}

/** Median of a numeric list; 0 for an empty list. */
export function medianOf(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : Math.floor((sorted[mid - 1] + sorted[mid]) / 2);
}

/**
 * Times to try during a poll: every advertised 5-min slot newer than the
 * newest accepted frame, oldest first (so the timeline never gets holes),
 * capped at `cap` per cycle. Slots are aligned to `endMs` — the service's
 * own newest advertised observation time.
 */
export function pollCandidateTimes(
  latestAcceptedMs: number,
  endMs: number,
  stepMs: number = FRAME_STEP_MS,
  cap: number = MAX_BACKFILL_PER_POLL
): number[] {
  const out: number[] = [];
  for (let t = endMs; t > latestAcceptedMs; t -= stepMs) {
    out.push(t);
  }
  out.reverse();
  return out.slice(0, cap);
}

export type Freshness = "fresh" | "aging" | "stale";

/**
 * Compute the list of frame timestamps (ascending, oldest → newest) ending at `endMs`.
 * Pure function of its inputs — no Date.now() usage.
 */
export function computeFrameTimes(
  endMs: number,
  count: number = FRAME_COUNT,
  stepMs: number = FRAME_STEP_MS
): number[] {
  if (count <= 0) return [];
  const times: number[] = [];
  for (let i = count - 1; i >= 0; i--) {
    times.push(endMs - i * stepMs);
  }
  return times;
}

/**
 * Given an existing ascending list of frame times and a new (more recent) end time,
 * append the new frame and drop the oldest, keeping the window length constant.
 * Used for the live-update rotation described in DESIGN.md §7.
 */
export function rotateFrames(existing: number[], newEndMs: number): number[] {
  if (existing.length === 0) return computeFrameTimes(newEndMs);
  const latest = existing[existing.length - 1];
  if (newEndMs <= latest) return existing;
  const rotated = [...existing.slice(1), newEndMs];
  return rotated;
}

export interface InitCandidate {
  timeMs: number;
  /** -1 = payload was not a decodable image. */
  pixelCount: number;
  byteSize: number;
}

/**
 * Select the initial display window from loaded candidates (ascending by
 * time). Trailing candidates that do not pass evaluateCandidate — empty,
 * partial, duplicate-of-previous or undecodable while still inside the
 * server's render-lag window — are dropped, then the newest `count` frames
 * are kept. Non-accepted frames in the middle are NOT dropped: by the time
 * older slots are re-examined they are mature, and a mature empty frame is
 * legitimate "no rain" data. If nothing is acceptable, the newest `count`
 * are returned unchanged — an all-empty window is displayed honestly rather
 * than reported as an error (see CandidateEvaluation docs for limits).
 */
export function selectInitialFrames(
  candidates: InitCandidate[],
  nowMs: number,
  count: number = FRAME_COUNT
): number[] {
  const median = medianOf(candidates.filter((c) => c.pixelCount > 0).map((c) => c.pixelCount));
  let end = candidates.length;
  while (end > 0) {
    const c = candidates[end - 1];
    const prev = end >= 2 ? candidates[end - 2] : undefined;
    const verdict = evaluateCandidate({
      pixelCount: c.pixelCount,
      byteSize: c.byteSize,
      ageMs: nowMs - c.timeMs,
      windowMedianPixels: median,
      prevPixelCount: prev?.pixelCount,
      prevByteSize: prev?.byteSize
    });
    if (verdict === "accept") break;
    end -= 1;
  }
  if (end === 0) {
    end = candidates.length;
  }
  return candidates.slice(Math.max(0, end - count), end).map((c) => c.timeMs);
}

/**
 * Classify freshness of the latest frame relative to "now" (DESIGN.md §9
 * thresholds). `freshMaxMs`/`agingMaxMs` default to the US thresholds above;
 * M003 passes each RadarSource's own `freshness` config (e.g. DWD's 10/30
 * min, DESIGN.md Aneks M003 §R3) instead.
 */
export function classifyFreshness(
  latestFrameMs: number,
  nowMs: number,
  freshMaxMs: number = FRESHNESS_FRESH_MAX_MS,
  agingMaxMs: number = FRESHNESS_AGING_MAX_MS
): Freshness {
  const age = nowMs - latestFrameMs;
  if (age <= freshMaxMs) return "fresh";
  if (age <= agingMaxMs) return "aging";
  return "stale";
}

/** Whole minutes elapsed between `fromMs` and `nowMs`, floored at 0. */
export function minutesAgo(fromMs: number, nowMs: number): number {
  return Math.max(0, Math.round((nowMs - fromMs) / 60000));
}

/**
 * Decide whether the set of successfully loaded frames is sufficient to operate
 * normally, per DESIGN.md §9 "Błąd pojedynczej klatki" rule (>= MIN_OK_FRAMES is fine).
 */
export function hasEnoughFrames(loadedCount: number, minOk: number = MIN_OK_FRAMES): boolean {
  return loadedCount >= minOk;
}

/** Format a Polish "X min temu" / "przed chwilą" relative-age label for the status chip. */
export function formatMinutesAgoLabel(minutes: number): string {
  if (minutes <= 0) return "przed chwilą";
  if (minutes === 1) return "1 min temu";
  return `${minutes} min temu`;
}

/**
 * Format the per-frame relative label shown under the scrubber timestamp,
 * relative to the newest frame in the current window (DESIGN.md §6 FrameTimestamp).
 * Returns null for the newest frame (caller shows a "NAJNOWSZA" badge instead).
 */
export function formatFrameRelativeLabel(frameMs: number, newestMs: number): string | null {
  const diffMin = Math.round((newestMs - frameMs) / 60000);
  if (diffMin <= 0) return null;
  return `(−${diffMin} min)`;
}
